import {
  useTheme,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useState } from 'react';
import moment from 'moment';
import Feedback from '../../components/special/Feedback';
import { API_DELETE, API_PATCH, DBRequest, useConfig } from '../../api/api';
import { tokens } from '../../global/theme/tokens';
import PreviewPositions from '../../components/preview/PreviewPositions';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import SpeedDialMenu from '../../components/menu/SpeedDialMenu';
import PrintIcon from '@mui/icons-material/Print';
import ShareIcon from '@mui/icons-material/Share';
import TextCard from '../../components/cards/TextCard';
import CardGrid from '../../components/cards/layout/CardGrid';
import FileCard from '../../components/cards/FilesCard';
import DownloadIcon from '@mui/icons-material/Download';
import CardContainer from '../../components/cards/layout/CardContainer';
import PopUpContainer from '../../components/popup/PopUpContainer';
import Share from '../../components/special/Share';
import RenderHTML from '../../components/preview/RenderHTML';
import StyledTextContainer from '../../components/preview/StyledTextContainer';
import SkeletonCard from '../../components/cards/SkeletonCard';
import { useTranslation } from 'react-i18next';
import ImagesCard from '../../components/cards/ImagesCard';
import ExoResendMail from '../../components/exo/ExoResendMail';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoCard from '../../components/cards/InfoCard';
import { InfoTypes } from './invoiceTypes';
import useFetch from '../../api/useFetch';
import NewInvoice from '../../components/new/NewInvoice';
import PictureNameCard from '../../components/cards/PictureNameCards/PictureNameCard';
import MailLogCard from '../../components/cards/mailLogCard/MailLogCard';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  finalInvoiceCode,
  progressBill,
  progressBillCodes,
} from '../../components/new/NewProgressBilling';
import { hasPermissions } from '../../auth/hasPermissions';
import TimeBillingCard from '../../components/cards/TimeBillingCard';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import ExoDocumentFromDraft from '../../components/exo/ExoDocumentFromDraft';

const ExtendInvoice = ({ row, onUpdate, onDelete }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const { apiData, setApiData } = useFetch(`invoices/${row.id}`);

  // feedback
  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: 'test',
  });
  function handleAlertOpen(text, type) {
    setAlertState({ alertOpen: true, alertText: text, alertType: type });
  }

  const [startData, setStartData] = useState(null);
  const [open, setOpen] = useState(false);

  // check if invoice is older than one month (only relevant for non-drafts)
  const invoiceDate = apiData?.data?.date ? moment(apiData.data.date) : null;
  const daysSinceInvoice = invoiceDate ? moment().diff(invoiceDate, 'days') : 0;
  const isDraft = apiData?.data?.isDraft;
  const isOlderThanOneMonth = !isDraft && daysSinceInvoice > 30;

  // menu
  const actions = [
    {
      icon: <ModeEditIcon />,
      name: t('Edit'),
      onClick: handleEdit,
      disabled: !hasPermissions('admin') && isOlderThanOneMonth,
      tooltipTitle:
        !hasPermissions('admin') && isOlderThanOneMonth
          ? t('Invoice too old to edit', { days: daysSinceInvoice })
          : t('Edit'),
    },
    {
      icon: <NoteAddIcon />,
      name: t('Finalize Invoice'),
      onClick: () => setOpenCreateInvoice(true),
      hidden: apiData && apiData.data.isDraft ? false : true, // only show if it's a draft
    },
    {
      icon: <ContentCopyIcon />,
      name: t('Copy'),
      onClick: handleCopy,
    },
    {
      icon: <ForwardToInboxIcon />,
      name: t('Send Again'),
      onClick: () => setOpenResendMail(true),
    },
    {
      icon: <PrintIcon />,
      name: t('Print'),
      onClick: () => handlePrint(apiData && apiData.data.file.temporaryUrl),
      hidden: apiData && apiData.data.file ? false : true,
    },
    {
      icon: <ShareIcon />,
      name: t('Share'),
      onClick: () => setOpenShare(true),
    },
    {
      icon: <DeleteOutlineIcon />,
      name: t('Delete'),
      onClick: handleDelete,
      hidden: !apiData?.data?.isDraft,
    },
  ];

  function handleDelete() {
    DBRequest({
      config,
      path: `invoices/${apiData.data.id}`,
      method: API_DELETE(),
      onFeedback: () => onDelete(row),
    });
  }

  function handleEdit() {
    setStartData(apiData.data);
    setOpen(true);
  }
  function handleCopy() {
    const dataCopied = {};
    apiData.data.positions.forEach((position) => {
      delete position.id;
    });
    dataCopied.positions = apiData.data.positions;
    dataCopied.note = apiData.data.note;
    dataCopied.title = apiData.data.title;

    setStartData(dataCopied);
    setOpen(true);
  }

  function handleUpdate(data) {
    setApiData(data);
    onUpdate(data.data);
  }

  // share
  const [openShare, setOpenShare] = useState(false);
  function handleCloseShare() {
    setOpenShare(!openShare);
  }

  // print
  function handlePrint(pdfUrl) {
    // Open a new window or iframe with the PDF URL
    const printWindow = window.open(pdfUrl, '_blank');

    // Wait for the PDF to load
    printWindow.onload = () => {
      // Trigger the print dialog
      printWindow.print();
    };
  }

  //rensend mail
  const [openResendMail, setOpenResendMail] = useState(false);

  // Create Invoice from a Draft
  const [openCreateInvoice, setOpenCreateInvoice] = useState(false);

  const invoiceType = progressBillCodes.includes(startData?.typeCode)
    ? 'progressBilling'
    : 'custom';

  return (
    <Box className="flex flex-col gap-4 relative">
      {!apiData ? (
        <InvoiceSkeleton />
      ) : (
        <>
          <ExoDocumentFromDraft
            dataCollection={apiData.data}
            recipient={apiData.data.recipient}
            type="invoices"
            id={apiData.data.id}
            open={openCreateInvoice}
            onClose={() => setOpenCreateInvoice(false)}
            handleUpdate={handleUpdate}
            askConfirmation
            previewDocument={'invoice'}
          />
          <ExoResendMail
            recipient={apiData.data.recipient}
            type="invoices"
            id={apiData.data.id}
            open={openResendMail}
            onClose={() => setOpenResendMail(false)}
          />
          <SpeedDialMenu actions={actions} />
          <TextCard text={apiData.data.title} />
          <CardGrid>
            <PictureNameCard
              data={apiData.data.recipient}
              title={t('Recipient')}
              pictureUrl={
                apiData.data.recipient.logo &&
                apiData.data.recipient.logo.thumbnailUrl
              }
              name={
                apiData.data.recipient.firstName
                  ? `${apiData.data.recipient.firstName} ${apiData.data.recipient.lastName}`
                  : apiData.data.recipient.name
              }
              type={apiData.data.recipientType}
            />
            <InfosAndStatus
              invoice={apiData.data}
              row={row}
              onUpdate={onUpdate}
              setInvoice={onUpdate}
              handleAlertOpen={handleAlertOpen}
            />

            <FileCard
              files={[
                {
                  label: t('Invoice'),
                  icon: <DownloadIcon />,
                  url: apiData.data.file ? apiData.data.file.temporaryUrl : '',
                },
                {
                  label: 'X-' + t('Invoice'),
                  icon: <DownloadIcon />,
                  url: apiData.data.xrechnung
                    ? apiData.data.xrechnung.temporaryUrl
                    : '',
                },
              ]}
            />
            <ImagesCard images={apiData.data.additionalFiles} />

            <MailLogCard span={2} logs={apiData.data.mailLogs} />

            {apiData.data.timeTrackingData && (
              <TimeBillingCard
                timeTrackingData={apiData.data.timeTrackingData}
                timeTrackings={apiData.data.timeTrackings || []}
                span={2}
              />
            )}

            <CardContainer
              span={88}
              sx={{
                display: 'block',
                padding: '0px',
              }}
            >
              <PreviewPositions positions={apiData.data.positions} />
            </CardContainer>

            {apiData.data.note && (
              <CardContainer span={88}>
                <StyledTextContainer
                  backgroundColor="transparent"
                  fullWidth
                  color={colors.text}
                >
                  <RenderHTML HTML={apiData.data.note} />
                </StyledTextContainer>
              </CardContainer>
            )}
            {apiData.data.internalNote && (
              <CardContainer span={99}>
                <StyledTextContainer backgroundColor="transparent" fullWidth>
                  <RenderHTML HTML={apiData.data.internalNote} />
                </StyledTextContainer>
              </CardContainer>
            )}
          </CardGrid>
        </>
      )}
      <NewInvoice
        open={open}
        onUpdate={handleUpdate}
        onClose={() => setOpen(false)}
        startData={startData}
        type={invoiceType}
      />
      <PopUpContainer open={openShare} onClose={handleCloseShare}>
        <Share
          title={`${t('Share Invoice')} ${apiData && apiData.data.nr}`}
          url={
            apiData && apiData.data.file ? apiData.data.file.temporaryUrl : ''
          }
        />
      </PopUpContainer>
      <Feedback setState={setAlertState} state={alertState} />
    </Box>
  );
};

const InfosAndStatus = ({
  invoice,
  onUpdate,
  setInvoice,
  handleAlertOpen,
  row,
}) => {
  const { t } = useTranslation();

  const [status, setStatus] = useState(invoice.status);
  const config = useConfig();

  function handleChange(e) {
    setStatus(e.target.value);
    if (e.target.value !== status) updateStatusInDB(e.target.value);
  }

  function updateStatusInDB(newStatus) {
    const request = { status: newStatus };

    DBRequest({
      config,
      path: `invoices/${invoice.id}/status`,
      method: API_PATCH(request),
      onResponse: () => {
        setInvoice({ ...invoice, status: newStatus });
        onUpdate({ ...row, status: newStatus });
      },
      onError: () => setStatus(row.status),
      onFeedback: handleAlertOpen,
    });
  }

  const customContent = (
    <FormControl fullWidth>
      <InputLabel id="demo-simple-select-label">{t('Status')}</InputLabel>
      <Select
        labelId="demo-simple-select-label"
        id="demo-simple-select"
        value={status}
        label="Status"
        onChange={handleChange}
      >
        <MenuItem value={0}>{t('Created')}</MenuItem>
        <MenuItem value={1}>{t('Payed')}</MenuItem>
        <MenuItem value={2}>{t('Sent')}</MenuItem>
        <MenuItem value={3}>{t('In Arrears')}</MenuItem>
        <MenuItem value={4}>{t('Refusal to Pay')}</MenuItem>
      </Select>
    </FormControl>
  );

  const infos = InfoTypes(invoice);

  return <InfoCard infos={infos} />;
};

const InvoiceSkeleton = () => {
  return (
    <CardGrid>
      <SkeletonCard height={54} span={4} />
      <SkeletonCard height={146} />
      <SkeletonCard height={146} />
      <SkeletonCard height={146} />
      <SkeletonCard height={146} />
      <SkeletonCard height={156} span={4} />
    </CardGrid>
  );
};

export default ExtendInvoice;
